import { Injectable } from '@angular/core';
import { IConnector, IInstallation, ISession, ISessionResult, IStation, SessionCmd, IUser, ErrorCodeValues, ErrorCode } from 'common_library';
import { AppService } from './app.service';
import { SessionService } from './entities/session.service';
import { AuthType } from 'common_library';
import { ToastService } from './utils/toast.service';
import { TranslateService } from '@ngx-translate/core';
import { Point } from 'geojson';
import { MainStateService } from './state/app-main-state.service';

export interface StateUI {
  station: IStation;
  connector?: IConnector;
  session?: ISession;
  isMine: boolean;
  notAvailable: boolean;
}
export interface IGeneralState {
  user?: {
    currentUser: IUser,
    session?: ISession,
  },
  selectedInstallation: IInstallation,
  selectedStation: IStation,
  selectedConnetor: IConnector,
  ui: {
    textColor: string,
    stateBackground: string,
    stateLabel: string,
  }
}
@Injectable({ providedIn: 'root' })
export class CoreService {

  constructor(
    private SeS: SessionService,
    public APP: AppService,
    public mainStateService: MainStateService,
    private toastService: ToastService,
    private translate: TranslateService
  ) { }

  async reserveConnector(connectorId: number, coord?: Point): Promise<ISessionResult> {
    return await this.SeS.requestCommand({
      cmd: SessionCmd.Reserve,
      connectorId,
      authType: AuthType.APP_USER,
      coord,
      clientId: this.mainStateService.getClientId()
    });
  }

  async cancelReservation(connectorId: number, sessionId: string): Promise<ISessionResult> {
    const rv = await this.SeS.requestCommand({
      cmd: SessionCmd.CancelReservation,
      connectorId,
      sessionId
    });
    return rv;
  }

  /**
   * `hostedHoldPaymentId`: hold pre-auth già pagato dall'utente sulla pagina del PSP.
   * Viaggia in `params` (stesso canale del rfidCode) ed è solo un puntatore: il
   * backend ri-verifica col PSP prima di adottarlo.
   */
  async startCharge(connectorId: number, sessionId: string, coord?: Point, hostedHoldPaymentId?: string): Promise<ISessionResult> {
    const rv = await this.SeS.requestCommand({
      cmd: SessionCmd.StartCharge,
      connectorId,
      carId: this.mainStateService.getSelectedUserCarForSession()?.id,
      sessionId,
      authType: AuthType.APP_USER,
      coord,
      clientId: this.mainStateService.getClientId(),
      params: hostedHoldPaymentId ? { hostedHoldPaymentId } : undefined
    });
    return rv;
  }

  async stopCharge(connectorId: number, sessionId: string): Promise<ISessionResult> {
    const rv = await this.SeS.requestCommand({
      cmd: SessionCmd.StopCharge,
      connectorId,
      sessionId
    });
    return rv;
  }

  async endSession(connectorId: number, sessionId: string): Promise<ISessionResult> {
    const rv = await this.SeS.requestCommand({
      cmd: SessionCmd.EndSession,
      connectorId,
      sessionId,
    });
    return rv;

  }

  async manageErrorCodes(errorCode: ErrorCodeValues, message?: string) {
    if (message) {
      await this.toastService.presentBasicToast({ message, color: 'warning', icon: 'warning' });
    }
    else if (errorCode === ErrorCode.CdcExpired) {
      await this.toastService.presentBasicToast({ message: this.translate.instant("CDC_AUTHENTICATION.CARD_EXPIRED"), color: 'danger', icon: 'card' });
    }
    else if (errorCode === ErrorCode.CdcNotValid) {
      await this.toastService.presentBasicToast({ message: this.translate.instant("CDC_AUTHENTICATION.PAYMENT_FAILED"), color: 'danger', icon: 'warning' });
    }
    else if (errorCode === ErrorCode.PreauthFailed) {
      await this.toastService.presentBasicToast({ message: this.translate.instant("CDC_AUTHENTICATION.PREAUTH_FAILED"), color: 'danger', icon: 'warning' });
    }
    else if (errorCode === ErrorCode.UnpaidSession) {
      await this.toastService.presentUnpaidSessionToast();
    }
    else if (errorCode === ErrorCode.OcpiSessionActive) {
      await this.toastService.presentBasicToast({ message: this.translate.instant("TOAST_MESSAGES.OCPI_SESSION_ACTIVE"), color: 'warning', icon: 'warning' });
    } else if (errorCode === ErrorCode.ConnectorNotAvailable) {
      await this.toastService.presentBasicToast({ message: this.translate.instant("TOAST_MESSAGES.CONNECTOR_NOT_AVAILABLE"), color: 'warning', icon: 'warning' });
    }
    // Stazione irraggiungibile ≠ connettore occupato: prima arrivava come ConnectorNotAvailable e il
    // messaggio mandava fuori strada (sembrava che il connettore fosse preso da altri).
    else if (errorCode === ErrorCode.StationOffline) {
      await this.toastService.presentBasicToast({ message: this.translate.instant("TOAST_MESSAGES.STATION_OFFLINE"), color: 'warning', icon: 'warning' });
    }
    else if (errorCode === ErrorCode.CmdRejected) {
      await this.toastService.presentBasicToast({ message: this.translate.instant("TOAST_MESSAGES.COMMAND_REJECTED_MESSAGE"), color: 'warning', icon: 'warning' });
    }
    else if (errorCode === ErrorCode.GeneralError) {
      await this.toastService.presentBasicToast({ message: this.translate.instant("TOAST_MESSAGES.UNKNOWN_ERROR"), color: 'danger', icon: 'warning' });
    }
    else if (errorCode === ErrorCode.ConnectorStateError) {
      await this.toastService.presentBasicToast({ message: this.translate.instant("TOAST_MESSAGES.CONNECTOR_STATE_ERROR"), color: 'danger', icon: 'warning' });
    }
    else {
      // Con un codice sconosciuto `key` restava undefined e ngx-translate lasciava a schermo il
      // segnaposto grezzo `#{{key}}`: si ripiega sul numero, che almeno è dicibile al supporto.
      const key = Object.keys(ErrorCode).find(k => ErrorCode[k] === errorCode) ?? String(errorCode);
      await this.toastService.presentBasicToast({ message: this.translate.instant("TOAST_MESSAGES.ERROR_WITH_CODE", { key }), color: 'danger', icon: 'warning' });
    }
  }
}
